import type { Rectangle } from "pixi.js";
import type { RefObject } from "react";
import type { CustomViewport } from "../CustomViewport";
import type { ManagedDisplayObject } from "../types";

interface FitVisibilityEffect {
    bounds: Rectangle;
    fade: boolean;
    fadeStartPaddingRatio: number;
    paddingRatio: number;
    ref: RefObject<ManagedDisplayObject | null>;
}

interface ConstantScreenSizeEffect {
    allowShrinkToFit: boolean;
    maxScale?: number;
    minScale?: number;
    ref: RefObject<ManagedDisplayObject | null>;
}

function isValidScaleLimit(scale: number | undefined): scale is number {
    return scale !== undefined && Number.isFinite(scale) && scale > 0;
}

export default class ViewportEffectsManager {
    private viewport: CustomViewport | null = null;
    private readonly constantScreenSizeEffects = new Map<
        RefObject<ManagedDisplayObject | null>,
        ConstantScreenSizeEffect
    >();
    private readonly fitVisibilityEffects = new Map<
        RefObject<ManagedDisplayObject | null>,
        FitVisibilityEffect
    >();
    private readonly onZoom: () => void;

    constructor() {
        this.onZoom = this.updateEffects.bind(this);
    }

    connect(viewport: CustomViewport): void {
        if (this.viewport === viewport) {
            this.updateEffects();
            return;
        }

        if (this.viewport) this.disconnect(this.viewport);

        this.viewport = viewport;

        viewport.on("zoomed", this.onZoom);
        viewport.on("zoomed-end", this.onZoom);
        this.updateEffects();
    }

    registerConstantScreenSize(
        ref: RefObject<ManagedDisplayObject | null>,
        minScale?: number,
        maxScale?: number,
        allowShrinkToFit = false,
    ): () => void {
        const effect = {
            ref,
            allowShrinkToFit,
            ...this.validateScaleLimits(minScale, maxScale),
        };
        this.constantScreenSizeEffects.set(ref, effect);
        this.updateConstantScreenSize(effect);
        this.updateFitVisibility(this.fitVisibilityEffects.get(ref));

        return () => {
            if (this.constantScreenSizeEffects.get(ref) === effect)
                this.constantScreenSizeEffects.delete(ref);
        };
    }

    registerVisibilityWhenFits(
        ref: RefObject<ManagedDisplayObject | null>,
        bounds: Rectangle,
        paddingRatio: number,
        fadeStartPaddingRatio: number,
        fade: boolean,
    ): () => void {
        const effect = {
            ref,
            bounds,
            paddingRatio,
            fadeStartPaddingRatio,
            fade,
        };
        this.fitVisibilityEffects.set(ref, effect);
        this.updateConstantScreenSize(this.constantScreenSizeEffects.get(ref));
        this.updateFitVisibility(effect);

        return () => {
            if (this.fitVisibilityEffects.get(ref) === effect)
                this.fitVisibilityEffects.delete(ref);
        };
    }

    private updateEffects(): void {
        for (const effect of this.constantScreenSizeEffects.values()) {
            this.updateConstantScreenSize(effect);
        }

        for (const effect of this.fitVisibilityEffects.values()) {
            this.updateFitVisibility(effect);
        }
    }

    private updateConstantScreenSize(
        effect: ConstantScreenSizeEffect | undefined,
    ): void {
        const object = effect?.ref.current;
        const viewport = this.viewport;
        if (!viewport?.isUsable || !effect || !object?.scale) return;

        const targetScale = 1 / viewport.scale.x;
        const minScale = this.getEffectiveMinScale(effect, object, targetScale);
        const scale = Math.min(
            Math.max(targetScale, minScale ?? -Infinity),
            effect.maxScale ?? Infinity,
        );
        object.scale.set(scale);
    }

    private getEffectiveMinScale(
        effect: ConstantScreenSizeEffect,
        object: ManagedDisplayObject,
        targetScale: number,
    ): number | undefined {
        const fitEffect = this.fitVisibilityEffects.get(effect.ref);
        if (
            !effect.allowShrinkToFit ||
            effect.minScale === undefined ||
            !fitEffect
        )
            return effect.minScale;

        const fullVisibilityScale = this.getFullVisibilityScale(
            object,
            fitEffect,
            targetScale,
        );
        return fullVisibilityScale === undefined
            ? effect.minScale
            : Math.min(effect.minScale, fullVisibilityScale);
    }

    private getFullVisibilityScale(
        object: ManagedDisplayObject,
        effect: FitVisibilityEffect,
        targetScale: number,
    ): number | undefined {
        if (!Number.isFinite(targetScale) || targetScale <= 0) return undefined;

        object.scale?.set(targetScale);
        const sizeRatio = this.getSizeRatio(object, effect.bounds);
        const fullVisibilityRatio = 1 - 2 * effect.paddingRatio;
        const fullVisibilityScale =
            (targetScale * fullVisibilityRatio) / sizeRatio;

        return Number.isFinite(fullVisibilityScale) && fullVisibilityScale > 0
            ? fullVisibilityScale
            : undefined;
    }

    private updateFitVisibility(effect: FitVisibilityEffect | undefined): void {
        const object = effect?.ref.current;
        if (!object || !effect) return;

        const sizeRatio = this.getSizeRatio(object, effect.bounds);
        const fullVisibilityRatio = 1 - 2 * effect.paddingRatio;
        const fadeStartRatio = 1 - 2 * effect.fadeStartPaddingRatio;
        if (
            !effect.fade ||
            effect.fadeStartPaddingRatio >= effect.paddingRatio
        ) {
            object.visible = sizeRatio <= fullVisibilityRatio;
            return;
        }

        const alpha =
            (fadeStartRatio - sizeRatio) /
            (fadeStartRatio - fullVisibilityRatio);
        object.alpha = Math.max(0, Math.min(1, alpha));
        object.visible = object.alpha > 0;
    }

    private getSizeRatio(
        object: ManagedDisplayObject,
        bounds: Rectangle,
    ): number {
        const width = object.width ?? 0;
        const height = object.height ?? 0;
        return Math.max(
            width > 0 ? width / bounds.width : 0,
            height > 0 ? height / bounds.height : 0,
        );
    }

    private validateScaleLimits(
        minScale?: number,
        maxScale?: number,
    ): Pick<ConstantScreenSizeEffect, "minScale" | "maxScale"> {
        const validMinScale = isValidScaleLimit(minScale)
            ? minScale
            : undefined;
        const validMaxScale = isValidScaleLimit(maxScale)
            ? maxScale
            : undefined;

        if (minScale !== undefined && validMinScale === undefined) {
            console.warn(
                "[useConstantScreenSize] Ignoring minScale: it must be finite and greater than zero.",
            );
        }

        if (maxScale !== undefined && validMaxScale === undefined) {
            console.warn(
                "[useConstantScreenSize] Ignoring maxScale: it must be finite and greater than zero.",
            );
        }

        if (
            validMinScale !== undefined &&
            validMaxScale !== undefined &&
            validMinScale > validMaxScale
        ) {
            console.warn(
                "[useConstantScreenSize] Ignoring scale limits: minScale must not exceed maxScale.",
            );
            return {};
        }

        return { minScale: validMinScale, maxScale: validMaxScale };
    }

    disconnect(viewport: CustomViewport): void {
        if (this.viewport !== viewport) return;

        viewport.off("zoomed", this.onZoom);
        viewport.off("zoomed-end", this.onZoom);
        this.viewport = null;
    }
}
